import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { enableProdMode, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { enableDebugTools , bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, provideRouter, withHashLocation } from '@angular/router';
import { reducers } from '@app-store/reducers';
import { CustomRouterStateSerializer } from '@app-store/serializers/CustomRouterStateSerializer';
import { HttpErrorInterceptor } from '@core/global-error-handler/http-error-interceptor';
import { RevRouteReuseStrategy } from '@core/route-reuse.strategy';
import { MODEL_STORE } from '@gandalf/model/model-store';
import { EffectsModule } from '@ngrx/effects';
import { RouterStateSerializer, StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { invoicesEffects } from '@invoices-store/effects';
import { reducers as InvoiceReducers } from '@invoices-store/reducers';
import { patientEffects } from '@patient-store/effects';
import { reducers as PatientReducers } from '@patient-store/reducers';
import { practiceEffects } from '@practice-store/effects';
import { reducers as PracticeReducers } from '@practice-store/reducers';
import { reducers as TaskReducers } from '@tasks-store/reducers';
import { GandalfStoreModule } from 'gandalf';
import { MorganaModule, SYNCFUSION_LICENSE } from 'morgana';
import { registerLicense } from '@syncfusion/ej2-base';
// eslint-disable-next-line no-restricted-imports
import { Dialog } from '@syncfusion/ej2-popups';
import { DropDownBase } from '@syncfusion/ej2-dropdowns';
// Static import forces froala-editor into the main esbuild chunk so plugins.pkgd can
// register on FroalaEditor synchronously; without this, angular-froala-wysiwyg's dynamic
// import() keeps it as a lazy chunk and the synchronous UMD require() returns undefined.
import 'froala-editor';
import 'froala-editor/js/plugins.pkgd.min.js';
import { ChartUtil, GridUtil } from 'majora';
import { PerformanceService } from '@core/performance/performance.service';
import { BusinessNamePipe } from '@shared/pipes/business-name/business-name.pipe';
import { PatientNamePipe } from '@shared/pipes/patient-name/patient-name.pipe';
import { PersonNamePipe } from '@shared/pipes/person-name/person-name.pipe';
import { HeaderComponent } from '@header/header.component';
import { AppComponent } from './app/app.component';
import { FooterComponent } from './app/footer/footer.component';
import { InjectorContainerModule } from './app/injector-container.module';
import { APP_ROUTES } from './app/app.routes';
import { environment } from './environments/environment';

// Syncfusion license registration
registerLicense(SYNCFUSION_LICENSE);

// Apply the same dialog defaults that ModalBaseDirective sets imperatively. In the old NgModule
// architecture MorganaModule exported ModalBaseDirective and it was automatically applied to every
// ejs-dialog in the app. In standalone mode each component must import it explicitly, which many
// don't. Overriding the prototype getters here replicates ModalBaseDirective.populateBaseProperties()
// as a global fallback. EJ2 components store explicitly-bound values in this.properties (via
// propertySetter). hasOwnProperty(this.properties, key) === false means the template never bound
// the property, so we can safely apply the default without overriding explicit bindings.
interface EJ2Props { properties: Record<string, unknown> }

(['showCloseIcon', 'isModal', 'allowDragging'] as const).forEach(prop => {
	const descriptor = Object.getOwnPropertyDescriptor(Dialog.prototype, prop);
	if (descriptor?.get) {
		Object.defineProperty(Dialog.prototype, prop, {
			...descriptor,
			get(this: EJ2Props) {
				return Object.prototype.hasOwnProperty.call(this.properties, prop)
					? this.properties[prop]
					: true;
			},
		});
	}
});

// Default cssClass to 'modal-md' (750px width) when not explicitly set — mirrors ModalBaseDirective.
const cssClassDescriptor = Object.getOwnPropertyDescriptor(Dialog.prototype, 'cssClass');
if (cssClassDescriptor?.get) {
	Object.defineProperty(Dialog.prototype, 'cssClass', {
		...cssClassDescriptor,
		get(this: EJ2Props) {
			if (Object.prototype.hasOwnProperty.call(this.properties, 'cssClass')) {
				return this.properties['cssClass'] || 'modal-md';
			}
			return 'modal-md';
		},
	});
}

// Default zIndex to 2000 (MODAL.Z_INDEX_STANDARD) when not explicitly set — mirrors ModalBaseDirective.
const zIndexDescriptor = Object.getOwnPropertyDescriptor(Dialog.prototype, 'zIndex');
if (zIndexDescriptor?.get) {
	Object.defineProperty(Dialog.prototype, 'zIndex', {
		...zIndexDescriptor,
		get(this: EJ2Props) {
			return Object.prototype.hasOwnProperty.call(this.properties, 'zIndex')
				? this.properties['zIndex']
				: 2000;
		},
	});
}

// Disable dialog open/close animations by default — mirrors ModalBaseDirective.
const animDescriptor = Object.getOwnPropertyDescriptor(Dialog.prototype, 'animationSettings');
if (animDescriptor?.get) {
	Object.defineProperty(Dialog.prototype, 'animationSettings', {
		...animDescriptor,
		get(this: EJ2Props) {
			return Object.prototype.hasOwnProperty.call(this.properties, 'animationSettings')
				? this.properties['animationSettings']
				: { effect: 'None' };
		},
	});
}
// Override the DropDownBase default field mapping so dropdowns use 'label'/'value' by default
// instead of Syncfusion's built-in 'text'/'text' default. All OptionItems in this codebase
// use 'label' for display text, so this restores correct display without needing [fields] on every dropdown.
const originalSetFields = (DropDownBase.prototype as any).setFields;
(DropDownBase.prototype as any).setFields = function(this: any) {
	if (!this.fields?.value && !this.fields?.text) {
		this.updateFields('label', 'value');
	} else {
		originalSetFields.call(this);
	}
};

// Default dialog position to top-center — mirrors ModalBaseDirective.
const positionDescriptor = Object.getOwnPropertyDescriptor(Dialog.prototype, 'position');
if (positionDescriptor?.get) {
	Object.defineProperty(Dialog.prototype, 'position', {
		...positionDescriptor,
		get(this: EJ2Props) {
			return Object.prototype.hasOwnProperty.call(this.properties, 'position')
				? this.properties['position']
				: { X: 'center', Y: 'top' };
		},
	});
}

// suppress the default ag-grid pagination panel since we use AgGridPaginationComponent instead
GridUtil.initGlobalGridOptions({
	suppressPaginationPanel: true,
});
ChartUtil.initGlobalChartOptions();

if (environment.production) {
	enableProdMode();
}

bootstrapApplication(AppComponent, {
	providers: [
		provideZoneChangeDetection(),
		provideAnimations(),
		provideRouter(APP_ROUTES, withHashLocation()),
		importProvidersFrom(
			MorganaModule,
			InjectorContainerModule,
			StoreModule.forRoot(reducers, {
				runtimeChecks: {
					strictStateImmutability: false,
					strictActionImmutability: false,
				},
			}),
			StoreModule.forFeature('patient', PatientReducers),
			StoreModule.forFeature('practice', PracticeReducers),
			StoreModule.forFeature('tasks', TaskReducers),
			StoreModule.forFeature('invoices', InvoiceReducers),
			// EffectsModule.forRoot must come before GandalfStoreModule, which uses EffectsModule.forFeature internally
			EffectsModule.forRoot([]),
			EffectsModule.forFeature(patientEffects),
			EffectsModule.forFeature(practiceEffects),
			EffectsModule.forFeature(invoicesEffects),
			GandalfStoreModule,
			StoreRouterConnectingModule.forRoot({ stateKey: 'router' }),
			StoreDevtoolsModule.instrument({
				name: 'PMS NgRx Devtools',
				maxAge: 25,
				logOnly: environment.production,
				connectInZone: true,
			}),
		),
		PatientNamePipe,
		PersonNamePipe,
		BusinessNamePipe,
		{ provide: 'MODEL_STORE', useValue: { ...MODEL_STORE } },
		{ provide: RouteReuseStrategy, useClass: RevRouteReuseStrategy },
		{ provide: RouterStateSerializer, useClass: CustomRouterStateSerializer },
		{ provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
		provideHttpClient(withInterceptorsFromDi()),
	],
}).then(appRef => {
	appRef.bootstrap(HeaderComponent);
	appRef.bootstrap(FooterComponent);

	// Enable the use of ng.profiler in the console
	const componentRef = appRef.components[0];
	enableDebugTools(componentRef);
	// Enable access to performanceService on the window object for use in the console
	window['performanceService'] = appRef.injector.get(PerformanceService);
}).catch(err => console.error(err));
